import type { NewPassioFood, PassioFood, PassioFoodResult, PassioFoodResults, PassioMeasuredPortion, SearchItemNutritionPreview } from "../models";

/** A nutrient amount per 100 g of the food. */
export interface NutrientEntry {
  name: string;
  shortName: string;
  unit: string;
  amount: number;
}

export interface Macros {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
}

type FoodLike = Pick<PassioFood, "nutrients" | "portions"> | NewPassioFood;

const UNIT_TO_G: Record<string, number> = { g: 1, gram: 1, grams: 1, kg: 1000, mg: 0.001, oz: 28.3495, lb: 453.592, ml: 1 };

/** Grams for one unit of a portion (100 g when unknown). */
export function portionWeightG(p?: PassioMeasuredPortion | null): number {
  const w = Number(p?.weight?.value);
  if (!p?.weight || !Number.isFinite(w) || w <= 0) return 100;
  return w * (UNIT_TO_G[String(p.weight.unit || "g").toLowerCase()] ?? 1);
}

/**
 * Suggested default quantity for a portion (first `suggestedQuantity`, else 1). `portion.quantity` is
 * the unit size, not a count, so it never multiplies full-detail nutrients.
 */
export function defaultQuantity(p?: PassioMeasuredPortion | null): number {
  const q = Number(p?.suggestedQuantity?.[0] ?? 1);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

/** `N = n * (w / 100) * q` for a per-100 g nutrient amount. */
export function nutrientForPortion(amountPer100g: number, p: PassioMeasuredPortion | null | undefined, quantity = defaultQuantity(p)): number {
  return ((Number(amountPer100g) || 0) * portionWeightG(p) * quantity) / 100;
}

/**
 * Grams a search / scan `nutritionPreview` describes. Previews are already scaled to their portion,
 * and there `portion.quantity` is a count (3 × 52 g eggs).
 */
export function previewGrams(preview: SearchItemNutritionPreview | null | undefined): number {
  if (!preview?.portion?.weight?.value) return 0;
  const q = Number(preview.portion.quantity);
  return portionWeightG(preview.portion) * (Number.isFinite(q) && q > 0 ? q : 1);
}

/** Rescale a preview's macros to another weight, e.g. the grams a scan predicted or the user edited. */
export function scalePreview(preview: SearchItemNutritionPreview | null | undefined, grams: number): Pick<Macros, "calories" | "protein" | "carbs" | "fat" | "fiber"> {
  const base = previewGrams(preview);
  const f = base ? grams / base : 0;
  const v = (x?: number) => (Number(x) || 0) * f;
  return { calories: v(preview?.calories), protein: v(preview?.protein), carbs: v(preview?.carbs), fat: v(preview?.fat), fiber: v(preview?.fiber) };
}

/** Add up macros (meal from items, day from meals). */
export function sumMacros<T extends Partial<Record<keyof Macros, number>>>(items: T[]): Macros {
  const out: Macros = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0 };
  for (const it of items) for (const k of Object.keys(out) as (keyof Macros)[]) out[k] += Number(it[k]) || 0;
  return out;
}

export function portionLabel(p?: PassioMeasuredPortion | null): string {
  if (!p) return "100 g";
  const w = portionWeightG(p);
  const name = p.name || "serving";
  return /^(g|gram|grams)$/i.test(name) ? `${name}` : `${name} (${fmtNumber(w, w < 10 ? 1 : 0)} g)`;
}

/** Normalise a refCode / barcode / packaged-product response into a header row plus its foods. */
export function foodsFromResult(data: PassioFoodResults | PassioFood | NewPassioFood | null | undefined): {
  header: PassioFoodResult | PassioFood | NewPassioFood | undefined;
  foods: FoodLike[];
} {
  if (!data) return { header: undefined, foods: [] };
  if ("results" in data || "alternatives" in data) {
    const row = (data as PassioFoodResults).results?.[0];
    if (!row) return { header: undefined, foods: [] };
    const own = (row as { nutrients?: unknown[] }).nutrients?.length ? [row as unknown as FoodLike] : [];
    return { header: row, foods: row.ingredients?.length ? row.ingredients : own };
  }
  return { header: data as PassioFood, foods: [data as PassioFood] };
}

const GRAM_PORTION: PassioMeasuredPortion = { name: "g", quantity: 100, weight: { unit: "g", value: 1 }, suggestedQuantity: [100] };
const isGramPortion = (p: PassioMeasuredPortion) => /^(g|gram|grams)$/i.test(p.name?.trim() ?? "");

/** Portions to offer for a hydrated food (header portions first, then the first food's). */
export function portionsFor(header: unknown, foods: FoodLike[]): PassioMeasuredPortion[] {
  const own = (header as { portions?: PassioMeasuredPortion[] } | undefined)?.portions ?? [];
  const list = own.length ? own : foods[0]?.portions ?? [];
  return list.length ? list : [{ ...GRAM_PORTION }];
}

/** The amount a result row showed. */
export interface AmountHint {
  nutritionPreview?: SearchItemNutritionPreview | null;
  weightGrams?: number | null;
  portionSize?: string | null;
  portionQuantity?: number | null;
}

/**
 * Portions (always including grams) plus the portion and quantity that reproduce the amount a row
 * displayed: its preview portion, which comes from this food's portion list. Without a preview,
 * the AI's suggested size when the food lists it, else the suggested grams. Without a usable hint
 * this is the first portion at its suggested quantity.
 */
export function startingPortion(portions: PassioMeasuredPortion[], hint?: AmountHint | null): { portions: PassioMeasuredPortion[]; index: number; quantity: number } {
  const list = portions.some(isGramPortion) ? portions : [...portions, { ...GRAM_PORTION }];
  const byName = (name?: string | null) => {
    const key = name?.trim().toLowerCase();
    return key ? list.findIndex((p) => p.name?.trim().toLowerCase() === key) : -1;
  };
  const gramIndex = list.findIndex(isGramPortion);

  const grams = previewGrams(hint?.nutritionPreview);
  if (grams > 0) {
    const match = byName(hint?.nutritionPreview?.portion?.name);
    if (match >= 0) {
      const q = Number(hint?.nutritionPreview?.portion?.quantity);
      return { portions: list, index: match, quantity: Number.isFinite(q) && q > 0 ? q : grams / portionWeightG(list[match]) };
    }
    return { portions: list, index: gramIndex, quantity: grams };
  }

  const rowGrams = Number(hint?.weightGrams) > 0 ? Number(hint?.weightGrams) : 0;
  const count = Number(hint?.portionQuantity) > 0 ? Number(hint?.portionQuantity) : 0;
  const sized = byName(hint?.portionSize);
  if (sized >= 0 && count) return { portions: list, index: sized, quantity: count };
  if (rowGrams) return { portions: list, index: gramIndex, quantity: rowGrams };
  return { portions: list, index: 0, quantity: defaultQuantity(list[0]) };
}

/**
 * Combined nutrient profile per 100 g. Single foods are used as-is (Passio nutrients are per
 * 100 g); recipes are weighted by each ingredient's default portion.
 */
export function nutrientProfilePer100g(foods: FoodLike[]): NutrientEntry[] {
  const map = new Map<string, NutrientEntry>();
  const add = (r: NonNullable<FoodLike["nutrients"]>[number], amount: number) => {
    const n = r.nutrient ?? {};
    const key = `${(n.name || n.shortName || "").toLowerCase()}|${n.unit || ""}`;
    const e = map.get(key) ?? { name: n.name || n.shortName || "Nutrient", shortName: n.shortName || "", unit: n.unit || "", amount: 0 };
    e.amount += amount;
    map.set(key, e);
  };
  if (foods.length === 1) {
    for (const r of foods[0].nutrients ?? []) add(r, Number(r.amount) || 0);
    return [...map.values()];
  }
  let grams = 0;
  for (const f of foods) {
    const p = f.portions?.[0];
    const g = portionWeightG(p) * defaultQuantity(p);
    grams += g;
    for (const r of f.nutrients ?? []) add(r, ((Number(r.amount) || 0) * g) / 100);
  }
  return [...map.values()].map((e) => ({ ...e, amount: grams ? (e.amount / grams) * 100 : 0 }));
}

export function findNutrient(profile: NutrientEntry[], aliases: string[], unit?: string): number {
  const want = aliases.map((a) => a.toLowerCase());
  const hit = profile.find(
    (e) => [e.shortName, e.name].some((x) => want.includes(String(x).toLowerCase())) && (!unit || e.unit.toLowerCase() === unit),
  );
  return hit?.amount ?? 0;
}

/** Key macros for `grams` of a food with the given per-100 g profile. */
export function macrosFor(profile: NutrientEntry[], grams: number): Macros {
  const s = (v: number) => (v * grams) / 100;
  return {
    calories: s(findNutrient(profile, ["calories", "energy"], "kcal") || findNutrient(profile, ["calories"])),
    protein: s(findNutrient(profile, ["protein"])),
    carbs: s(findNutrient(profile, ["carbs", "carbohydrate", "carbohydrates", "carbohydrate, by difference"])),
    fat: s(findNutrient(profile, ["fat", "total fat", "total lipid (fat)"])),
    fiber: s(findNutrient(profile, ["fiber", "dietary fiber", "fiber, total dietary"])),
    sugar: s(findNutrient(profile, ["sugartotal", "sugars", "sugar", "sugars, total"])),
    sodium: s(findNutrient(profile, ["sodium", "sodium, na"])),
  };
}

/** Share of calories from protein / carbs / fat (0-100, sums to ~100). */
export function macroSplit(m: Pick<Macros, "protein" | "carbs" | "fat">) {
  const p = m.protein * 4, c = m.carbs * 4, f = m.fat * 9;
  const t = p + c + f || 1;
  return { protein: Math.round((p / t) * 100), carbs: Math.round((c / t) * 100), fat: Math.round((f / t) * 100) };
}

export const NUTRIENT_GROUPS = ["Energy & macros", "Fats breakdown", "Carbs breakdown", "Vitamins", "Minerals", "Other"] as const;
export type NutrientGroup = (typeof NUTRIENT_GROUPS)[number];

export function nutrientGroup(e: NutrientEntry): NutrientGroup {
  const s = `${e.name} ${e.shortName}`.toLowerCase();
  if (/saturated|monounsat|polyunsat|trans|cholesterol|omega|fatty acid/.test(s)) return "Fats breakdown";
  if (/sugar|fiber|fibre|starch|sugar alcohol/.test(s)) return "Carbs breakdown";
  if (/vitamin|folate|folic|niacin|riboflavin|thiamin|biotin|pantothenic|choline|retinol|carotene|tocopherol/.test(s)) return "Vitamins";
  if (/calcium|iron|magnesium|phosphorus|potassium|sodium|zinc|copper|manganese|selenium|iodine|chromium|molybdenum|fluoride/.test(s)) return "Minerals";
  if (/energy|calorie|protein|carb|fat|lipid|water|alcohol/.test(s)) return "Energy & macros";
  return "Other";
}

export function groupNutrients(profile: NutrientEntry[]): { group: NutrientGroup; entries: NutrientEntry[] }[] {
  return NUTRIENT_GROUPS.map((group) => ({ group, entries: profile.filter((e) => nutrientGroup(e) === group) })).filter(
    (g) => g.entries.length,
  );
}

export function unitLabel(u: string): string {
  return ({ KCAL: "kcal", G: "g", MG: "mg", UG: "µg", MCG: "µg", IU: "IU", KJ: "kJ" } as Record<string, string>)[u.toUpperCase()] ?? u.toLowerCase();
}

export function fmtNumber(n: number | undefined | null, digits = 0): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return "–";
  return n.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

export type IconSize = 90 | 180 | 360;

/** Passio food icon CDN URL. Not every id has an icon, so handle image load errors. */
export function foodIconUrl(iconId: string | undefined, size: IconSize = 90): string | undefined {
  if (!iconId) return undefined;
  return `https://storage.googleapis.com/passio-prod-env-public-cdn-data/label-icons/${encodeURIComponent(iconId)}-${size}.jpg`;
}

/** Allergen-style tags (`contains_peanuts`) as display strings. */
export function allergenLabels(tags: string[] | undefined): string[] {
  return (tags ?? [])
    .filter((t) => /^contains_/i.test(t))
    .map((t) => t.replace(/^contains_/i, "").replace(/_/g, " "));
}
